import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Users, Package, FileText, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setData(r.data)).catch(() => setData({ clients: 0, programs: 0, bookings: 0, revenue: 0, recent_bookings: [] }));
  }, []);

  const stats = [
    { key: "totalClients", value: data?.clients ?? 0, icon: Users, color: "text-blue-600 bg-blue-50" },
    { key: "totalPrograms", value: data?.programs ?? 0, icon: Package, color: "text-emerald-600 bg-emerald-50" },
    { key: "totalBookings", value: data?.bookings ?? 0, icon: FileText, color: "text-purple-600 bg-purple-50" },
    { key: "totalRevenue", value: `$${(data?.revenue ?? 0).toLocaleString()}`, icon: TrendingUp, color: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="uppercase-label">{t("dashboard")}</div>
        <h1 className="display-font text-3xl md:text-4xl font-bold text-[hsl(var(--primary))] mt-1">
          {t("appName")}
        </h1>
        <div className="mt-3 divider-accent" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.key} className="dashboard-card fade-up" data-testid={`stat-${s.key}`}>
            <div className="flex items-start justify-between">
              <div className={"w-10 h-10 rounded-lg grid place-items-center " + s.color}>
                <s.icon size={18} />
              </div>
            </div>
            <div className="mt-4 uppercase-label">{t(s.key)}</div>
            <div className="stat-value mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="uppercase-label">{t("recentBookings")}</div>
            <h3 className="display-font text-lg font-semibold text-[hsl(var(--primary))]">
              {t("bookings")}
            </h3>
          </div>
        </div>

        {(data?.recent_bookings?.length ?? 0) === 0 ? (
          <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
            {t("empty")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("refNo")}</TableHead>
                <TableHead>{t("client")}</TableHead>
                <TableHead>{t("program")}</TableHead>
                <TableHead>{t("startDate")}</TableHead>
                <TableHead>{t("status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_bookings.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="font-mono text-xs">{b.ref_no}</TableCell>
                  <TableCell>{b.client?.name || "—"}</TableCell>
                  <TableCell>{b.program?.name || "—"}</TableCell>
                  <TableCell>{b.start_date || "—"}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700">
                      {b.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
